import { Outlet, createFileRoute } from "@tanstack/react-router"
import { Group, Burger, Divider, Center, Loader } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { InstanceLogoLink } from "./-components/instance-logo"
import { NavigationTree } from "./-components/navigation-tree"
import { UserSectionContent } from "./-components/user-section-content"
import { useBreakpoints } from "../utils/use-breakpoints"
import { AttachmentUploadContextProvider } from "../attachment-api/components/attachment-upload-context-provider"
import { ScrollContainer } from "../-components/scroll-container"
import "./index.css"
import { useQueryGet } from "@/typed-client/hooks/use-query-get"
import { useSuspenseQueryGet } from "@/typed-client/hooks/use-suspense-query-get"
import { ensureClientPushSubscription } from "@/utils/subscriptions"
import {
  chatHubConnection,
  initializeSignalR,
} from "./-components/chat/chatHub"
import {
  SuspenseWithErrorBoundary,
  SuspenseWithFallback,
} from "@components/suspense"

export const Route = createFileRoute("/app")({
  component: () => (
    <SuspenseWithFallback>
      <Shell />
    </SuspenseWithFallback>
  ),
})

export function Shell() {
  const ensurePush = React.useRef<boolean>(true)
  const navigate = Route.useNavigate()
  const { xs } = useBreakpoints({
    xs: { defaultValue: true, options: { getInitialValueInEffect: false } },
  })
  const { data: navigationInfo } = useSuspenseQueryGet(
    "/api/NavigationTree/GetNavigationInfo",
    null
  )
  const { data: authStatus } = useSuspenseQueryGet(
    "/api/Authentication/GetAuthStatus",
    null
  )
  const { data: features } = useQueryGet(
    "/api/Settings/GetFeatureSettings",
    null,
    { enabled: authStatus.loggedIn }
  )
  const [opened, { toggle, close }] = useDisclosure(!xs)

  React.useEffect(() => {
    if (authStatus.loggedIn) {
      if (
        (features?.chatEnabled || features?.feedEnabled) &&
        !chatHubConnection.connectionId
      ) {
        initializeSignalR()
      }
      if (
        chatHubConnection.connectionId &&
        !(features?.feedEnabled || features?.chatEnabled)
      ) {
        chatHubConnection.stop()
      }
      try {
        if (ensurePush.current && Notification.permission !== "denied") {
          if (Notification.permission === "default") {
            Notification.requestPermission()
          }
          ensureClientPushSubscription()
          ensurePush.current = false
        }
      } catch (err) {
        console.error(err)
      }
    }
  }, [authStatus.loggedIn, features])

  React.useEffect(() => {
    if (authStatus.requireMfaValidation) {
      navigate({
        to: "/authenticate/mfa/select-authentication-factor",
        search: { returnUrl: undefined },
      })
    } else {
      if (location.pathname === "/app" || location.pathname === "/app/") {
        navigate({
          to: navigationInfo.permittedRoutes[0]?.route ?? "/authenticate/login",
          replace: true,
        })
      }
    }
  }, [authStatus.requireMfaValidation, navigationInfo.permittedRoutes])

  // avoid rendering any app internal components before redirect to mfa validation
  if (authStatus.requireMfaValidation) {
    return null
  }

  return (
    <AttachmentUploadContextProvider>
      <div className="shell-root">
        {xs && (
          <>
            <div className="shell-header">
              <Group
                h="100%"
                px="xs"
                justify="space-between"
                style={{ flexDirection: "row-reverse" }}
              >
                <Burger opened={opened} onClick={toggle} />
                <InstanceLogoLink />
              </Group>
            </div>
            <Divider />
          </>
        )}

        <div className="shell-body">
          <aside
            className="shell-sidebar"
            data-opened={opened}
            data-mobile={xs}
            style={{
              width: opened ? (xs ? "100%" : 300) : xs ? 0 : 60,
            }}
          >
            {!xs && (
              <>
                <Group
                  h="60px"
                  px="sm"
                  justify="space-between"
                  style={{ flexDirection: "row-reverse" }}
                >
                  <Burger opened={opened} onClick={toggle} />
                  {(opened || xs) && <InstanceLogoLink />}
                </Group>
                <Divider />
              </>
            )}

            <div className="shell-sidebar-content">
              <ScrollContainer
                flex={1}
                direction="column"
                style={{ overflowX: "hidden" }}
              >
                <SuspenseWithErrorBoundary>
                  <NavigationTree
                    navigationTree={navigationInfo.navTree}
                    iconsOnly={!opened && !xs}
                    onNodeClick={xs ? close : undefined}
                  />
                </SuspenseWithErrorBoundary>
              </ScrollContainer>
              <Divider />
              <UserSectionContent
                onNavigate={() => {
                  if (xs && opened) {
                    close()
                  }
                }}
                iconOnly={!opened}
              />
            </div>
          </aside>
          <Divider orientation="vertical" />

          <main className="shell-main">
            <SuspenseWithErrorBoundary
              fallback={
                <Center style={{ height: "100%" }}>
                  <Loader size={30} />
                </Center>
              }
            >
              <Outlet />
            </SuspenseWithErrorBoundary>
          </main>
        </div>
      </div>
    </AttachmentUploadContextProvider>
  )
}
